import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, VariantProps } from 'class-variance-authority';
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  createContext,
  ElementRef,
  forwardRef,
  HTMLAttributes,
  useContext,
} from 'react';
import { cn } from '../../lib/utils';
import { ChevronLeft, Close } from '../../icons';
import { IconButton } from '../icon-button/icon-button';
import { useModalControlContext } from '../modal/modal-control-context';
import { useIsScrolledContext } from '../modal/is-scrolled-context';

export { RoundedModalHeaderIllustration as SheetHeaderIllustration } from '../modal/rounded-modal/rounded-modal-header-illustration';

// ─── Align context — shared between SheetHeader, SheetTitle, SheetDescription ─

type SheetHeaderAlign = 'left' | 'center';

const SheetHeaderAlignContext = createContext<SheetHeaderAlign>('left');

const useSheetHeaderAlign = () => useContext(SheetHeaderAlignContext);

// ─── Shared CVA helpers ───────────────────────────────────────────────────────

const textAlignVariants = cva('text-left', {
  variants: {
    alignContent: {
      left: 'text-left',
      center: 'text-center',
    },
  },
});

// ─── SheetHeader ──────────────────────────────────────────────────────────────

type SheetHeaderProps = HTMLAttributes<HTMLDivElement> & {
  /** Aligns title, description, and illustration inside the header. Defaults to "left". */
  alignContent?: SheetHeaderAlign;
  showCloseButton?: boolean;
  closeButtonProps?: ComponentPropsWithoutRef<typeof SheetCloseButton>;
  onBackButtonClick?: () => void;
  backButtonProps?: ComponentPropsWithoutRef<typeof IconButton>;
  variant?: 'default' | 'horizontal';
};

export const SheetHeader = ({
  className,
  children,
  alignContent = 'left',
  showCloseButton = true,
  closeButtonProps,
  onBackButtonClick,
  backButtonProps,
  variant = 'default',
  ...props
}: SheetHeaderProps) => {
  const { isContentScrolled } = useIsScrolledContext();

  return (
    <SheetHeaderAlignContext.Provider value={alignContent}>
      <div
        data-slot="sheet-header"
        data-scrolled={isContentScrolled}
        className={cn(
          'px-6 pt-6 pb-3',
          'border-b border-b-transparent transition-colors data-[scrolled=true]:border-space-15',
          className,
        )}
        {...props}>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-x-3 gap-y-2">
          {/* BACK BUTTON or left button can be overridden with backButtonProps */}
          <div className="col-span-1 col-start-1 row-start-1 flex justify-start">
            {onBackButtonClick && (
              <IconButton
                size="sm"
                variant="grey"
                icon={ChevronLeft}
                onClick={onBackButtonClick}
                {...backButtonProps}
              />
            )}
          </div>
          {/* CLOSE BUTTON or right button can be overridden with closeButtonProps */}
          <div className="col-span-1 col-start-3 row-start-1 flex justify-end">
            {showCloseButton && <SheetCloseButton {...closeButtonProps} />}
          </div>
          <div
            className={cn(
              'flex flex-col items-start gap-2',
              textAlignVariants({ alignContent }),
              alignContent === 'center' && 'items-center',
              variant === 'default' && 'col-span-3 col-start-1 row-start-2',
              variant === 'horizontal' && 'col-span-1 col-start-2 row-start-1',
            )}>
            {children}
          </div>
        </div>
      </div>
    </SheetHeaderAlignContext.Provider>
  );
};

// ─── SheetTitle ───────────────────────────────────────────────────────────────

const sheetTitleVariants = cva('', {
  variants: {
    variant: {
      coupleside: 'display-2xl',
      b2b: 'typo-2xl-semibold',
    },
    alignContent: {
      left: 'text-left',
      center: 'text-center',
    },
  },
});

type SheetTitleProps = ComponentProps<typeof DialogPrimitive.Title> & {
  variant?: NonNullable<VariantProps<typeof sheetTitleVariants>['variant']>;
};

export const SheetTitle = ({
  className,
  children,
  variant = 'coupleside',
  ...props
}: SheetTitleProps) => {
  const alignContent = useSheetHeaderAlign();
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        'relative m-0 text-space',
        sheetTitleVariants({ variant, alignContent }),
        className,
      )}
      {...props}>
      {children}
    </DialogPrimitive.Title>
  );
};

// ─── SheetDescription ─────────────────────────────────────────────────────────

export const SheetDescription = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => {
  const alignContent = useSheetHeaderAlign();
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn(
        'm-0 text-left typo-base text-space',
        textAlignVariants({ alignContent }),
        className,
      )}
      {...props}
    />
  );
};

// ─── SheetCloseButton ─────────────────────────────────────────────────────────

export const SheetCloseButton = forwardRef<
  ElementRef<typeof IconButton>,
  Omit<ComponentPropsWithoutRef<typeof IconButton>, 'icon'>
>(({ className, ...props }, ref) => {
  const { close } = useModalControlContext();
  return (
    <IconButton
      variant="grey"
      size="sm"
      icon={Close}
      onClick={close}
      ref={ref}
      className={className}
      {...props}
    />
  );
});
SheetCloseButton.displayName = 'SheetCloseButton';
